import { type ISize, point, segment, vector } from '../../../elements/index';
import { getRectangleHitArea } from '../../../elements/rectangle';
import { ARC_SMALL_LEFT, SvgPathBuilder } from '../../../svg/SvgPathBuilder';
import { makeTag } from '../../../svg/index';
import { wrapInSvgTag } from '../../../svg/wrapInSvgTag';
import { roundTo2, squared } from '../../../utils/numbers';
import {
  type NodeIconOptions,
  type NodeLabelStyle,
  type NodeTypeIcon,
  nodeIconDefaultOptions,
} from '../NodeTypeIcon';
import { getCloudyColors, makeGetCloudyClippingPoint } from '../cloudyCommon';

const HEIGHT = 60;
const WIDTH = 100;
const CIRCLES_RADIUS = 20;
const ICON_STROKE_WIDTH = 3;

const circles = [
  point.create(-28, 8),
  point.create(-13, -8),
  point.create(13, -8),
  point.create(28, 8),
];

const circleIntersections = [
  point.create(-28, 28), // bottom line -> circle 0
  point.create(-32.7, -11.44), // circle 0 -> 1
  point.create(0, -23.2), // circle 1 -> 2
  point.create(32.7, -11.44), // circle 2 -> 3
  point.create(28, 28), // circle 3 -> bottom line
] as const;

const cloudBottomSegment = segment.createNonZero(
  vector.add(circleIntersections[0], { x: 0, y: ICON_STROKE_WIDTH / 2 }),
  vector.add(circleIntersections[4], { x: 0, y: ICON_STROKE_WIDTH / 2 }),
);

function createCloudIcon(options: NodeIconOptions = {}): string {
  const opts = { ...nodeIconDefaultOptions, ...options };

  const path = new SvgPathBuilder().moveTo(circleIntersections[0]);
  circleIntersections
    .slice(1)
    .forEach(path.circleTo(CIRCLES_RADIUS, ARC_SMALL_LEFT));

  const { fill, stroke } = getCloudyColors(opts.style);

  const icon = makeTag('path', {
    d: path.buildClosed(),
    fill,
    stroke,
    'stroke-width': ICON_STROKE_WIDTH,
  });

  return wrapInSvgTag(icon, {
    width: Math.round(WIDTH * opts.scale),
    height: Math.round(HEIGHT * opts.scale),
    viewBox: `${-roundTo2(WIDTH / 2)} ${-roundTo2(HEIGHT / 2)} ${roundTo2(WIDTH)} ${roundTo2(HEIGHT)}`,
    ...opts.attributes,
  });
}

const getCloudClippingPoint = makeGetCloudyClippingPoint({
  circles: circles.map((center) => [
    center,
    CIRCLES_RADIUS + ICON_STROKE_WIDTH / 2,
  ]),
  segments: [cloudBottomSegment],
});

const cloudIconLabelStyle: NodeLabelStyle = {
  anchor: { x: 0.5, y: 0.5 },
  offset: { x: 0, y: HEIGHT / 12 },
};

const cloudIconSize: ISize = { width: WIDTH, height: HEIGHT };

// approximately rectangular, not worth exact calculation
const CLOUD_ICON_MAX_RADIUS = Math.sqrt(
  squared(WIDTH / 2) + squared(HEIGHT / 2),
);

export const cloudIcon: NodeTypeIcon = {
  createIconSvg: createCloudIcon,
  makeGetClippingPoint: () => getCloudClippingPoint,
  getHitArea: () => getRectangleHitArea(WIDTH, HEIGHT),
  getLabelStyle: () => cloudIconLabelStyle,
  getMaxRadius: () => CLOUD_ICON_MAX_RADIUS,
  getSize: () => cloudIconSize,
  shape: 'cloud',
};
